import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaFilePdf, FaEnvelope, FaUserCircle, FaHome } from 'react-icons/fa';

const Confirmation = ({ context }) => {
  const { bookingDetails } = context;
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    try {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      setIsLoggedIn(!!(token || userData));
    } catch (error) {
      console.error('Error checking auth status:', error);
      setIsLoggedIn(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  if (!bookingDetails) {
    return (
      <div className="container mt-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading booking details...</p>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="card confirmation-card">
        <div className="card-header bg-primary text-white">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="mb-0">Booking Confirmation</h2>
            <button 
              className="btn btn-light btn-sm"
              onClick={handleHomeClick}
            >
              <FaHome className="me-1" /> Home
            </button>
          </div>
        </div>
        
        <div className="card-body">
          {/* User Dashboard Button */}
          {isLoggedIn && (
            <div className="mb-4 text-end">
              <a 
                href="/user/dashboard" 
                className="btn btn-info"
              >
                <FaUserCircle className="me-2" />
                Go to User Dashboard
              </a>
            </div>
          )}

          {/* Confirmation Header */}
          <div className="confirmation-header mb-4">
            <h3 className="text-success">
              <i className="fas fa-check-circle me-2"></i>
              Booking Confirmed!
            </h3>
            <p className="text-muted">
              Your booking reference: <strong>{bookingDetails._id || 'N/A'}</strong>
            </p>
          </div>

          <div className="row">
            {/* Trip Details Column */}
            <div className="col-md-6">
              <div className="confirmation-section">
                <h4 className="section-title">
                  <i className="fas fa-route me-2"></i>
                  Trip Details
                </h4>
                
                <div className="detail-item">
                  <span className="detail-label">Vehicle:</span>
                  <span className="detail-value">{bookingDetails.vehicleName || 'N/A'}</span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">Pickup:</span>
                  <span className="detail-value">{bookingDetails.pickupLocation || 'N/A'}</span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">Dropoff:</span>
                  <span className="detail-value">{bookingDetails.dropoffLocation || 'N/A'}</span>
                </div>
                
                {bookingDetails.stops && bookingDetails.stops.length > 0 && (
                  <div className="detail-item">
                    <span className="detail-label">Stops:</span>
                    <span className="detail-value">
                      {bookingDetails.stops.join(', ')}
                    </span>
                  </div>
                )}
                
                <div className="detail-item">
                  <span className="detail-label">Date:</span>
                  <span className="detail-value">{formatDate(bookingDetails.pickupDate)}</span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">Time:</span>
                  <span className="detail-value">{formatTime(bookingDetails.pickupTime)}</span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">Passengers:</span>
                  <span className="detail-value">{bookingDetails.numberOfPassengers || 'N/A'}</span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">Luggage:</span>
                  <span className="detail-value">{bookingDetails.numberOfLuggage || 'N/A'}</span>
                </div>
                
                {bookingDetails.flightNumber && (
                  <div className="detail-item">
                    <span className="detail-label">Flight Number:</span>
                    <span className="detail-value">{bookingDetails.flightNumber}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Payment Details Column */}
            <div className="col-md-6">
              <div className="confirmation-section">
                <h4 className="section-title">
                  <i className="fas fa-credit-card me-2"></i>
                  Payment Details
                </h4>
                
                <div className="detail-item">
                  <span className="detail-label">Total Amount:</span>
                  <span className="detail-value">${(bookingDetails.totalAmount || 0).toFixed(2)}</span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">Payment Method:</span>
                  <span className="detail-value text-capitalize">
                    {bookingDetails.paymentMethod || 'N/A'}
                    {bookingDetails.paymentMethod === 'card' && bookingDetails.paymentDetails && (
                      <span className="text-muted ms-2">
                        (•••• {bookingDetails.paymentDetails.cardLast4})
                      </span>
                    )}
                  </span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">Payment Status:</span>
                  <span className={`detail-value ${
                    bookingDetails.paymentStatus === 'Completed' ? 'text-success' : 'text-warning'
                  }`}>
                    {bookingDetails.paymentStatus || 'N/A'}
                  </span>
                </div>
                
                {bookingDetails.couponCode && (
                  <div className="detail-item">
                    <span className="detail-label">Coupon Applied:</span>
                    <span className="detail-value">
                      {bookingDetails.couponCode} (${(bookingDetails.couponDiscount || 0).toFixed(2)} discount)
                    </span>
                  </div>
                )}
              </div>
              
              {/* Invoice Section */}
              {bookingDetails.invoice && (
                <div className="confirmation-section mt-4">
                  <h4 className="section-title">
                    <FaFilePdf className="me-2" />
                    Your Invoice
                  </h4>
                  <div className="d-flex align-items-center">
                    <a 
                      href={bookingDetails.invoice} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-success me-3"
                    >
                      <FaFilePdf className="me-2" />
                      Download Invoice
                    </a>
                    <span className="text-muted">
                      <FaEnvelope className="me-1" />
                      Also sent to {bookingDetails.customerEmail}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Return Trip Section */}
          {bookingDetails.isReturnBooking && (
            <div className="confirmation-section mt-4">
              <h4 className="section-title">
                <i className="fas fa-exchange-alt me-2"></i>
                Return Trip Details
              </h4>
              
              <div className="row">
                <div className="col-md-6">
                  <div className="detail-item">
                    <span className="detail-label">Vehicle:</span>
                    <span className="detail-value">{bookingDetails.returnVehicleName || 'N/A'}</span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">Pickup:</span>
                    <span className="detail-value">{bookingDetails.returnPickupLocation || 'N/A'}</span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">Dropoff:</span>
                    <span className="detail-value">{bookingDetails.returnDropoffLocation || 'N/A'}</span>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="detail-item">
                    <span className="detail-label">Date:</span>
                    <span className="detail-value">{formatDate(bookingDetails.returnPickupDate)}</span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">Time:</span>
                    <span className="detail-value">{formatTime(bookingDetails.returnPickupTime)}</span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">Passengers:</span>
                    <span className="detail-value">{bookingDetails.returnNumberOfPassengers || 'N/A'}</span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">Luggage:</span>
                    <span className="detail-value">{bookingDetails.returnNumberOfLuggage || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Addons Section */}
          {(bookingDetails.addons?.babySeat || bookingDetails.addons?.wineBottle || bookingDetails.addons?.redCarpet) && (
            <div className="confirmation-section mt-4">
              <h4 className="section-title">
                <i className="fas fa-plus-circle me-2"></i>
                Addons
              </h4>
              
              <div className="row">
                {bookingDetails.addons.babySeat && (
                  <div className="col-md-6">
                    <div className="detail-item">
                      <span className="detail-label">Child Seats:</span>
                      <span className="detail-value">
                        ${(bookingDetails.addons.babySeatDetails?.totalBabySeatPrice || 0).toFixed(2)}
                      </span>
                    </div>
                    <ul className="addon-details">
                      {bookingDetails.addons.babySeatDetails.infant.selected && (
                        <li>
                          Infant: {bookingDetails.addons.babySeatDetails.infant.quantity} × ${bookingDetails.addons.babySeatDetails.infant.price}
                        </li>
                      )}
                      {bookingDetails.addons.babySeatDetails.child.selected && (
                        <li>
                          Child: {bookingDetails.addons.babySeatDetails.child.quantity} × ${bookingDetails.addons.babySeatDetails.child.price}
                        </li>
                      )}
                      {bookingDetails.addons.babySeatDetails.booster.selected && (
                        <li>
                          Booster: {bookingDetails.addons.babySeatDetails.booster.quantity} × ${bookingDetails.addons.babySeatDetails.booster.price}
                        </li>
                      )}
                    </ul>
                  </div>
                )}
                
                {bookingDetails.addons.wineBottle && (
                  <div className="col-md-6">
                    <div className="detail-item">
                      <span className="detail-label">Wine Bottle:</span>
                      <span className="detail-value">
                        ${(bookingDetails.addons.wineBottlePrice || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
                
                {bookingDetails.addons.redCarpet && (
                  <div className="col-md-6">
                    <div className="detail-item">
                      <span className="detail-label">Red Carpet:</span>
                      <span className="detail-value">
                        ${(bookingDetails.addons.redCarpetPrice || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Confirmation Message */}
          <div className="alert alert-success mt-4 confirmation-message">
            <div className="d-flex align-items-center">
              <FaEnvelope className="me-3 fs-4" />
              <div>
                <h5>Booking Confirmed!</h5>
                <p className="mb-0">
                  A confirmation email with your invoice has been sent to <strong>{bookingDetails.customerEmail}</strong>.
                  Please check your inbox and spam folder if you don't see it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Confirmation;